import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { registerVendor, getVendorPrefill, RegisterVendorPayload } from '../api/client';

type FormData = Omit<RegisterVendorPayload, 'token' | 'otpCode' | 'noInvoiceFlag' | 'accountType'> & {
  noInvoiceFlag: boolean;
  accountType: string;
};

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // v0.13 要件4: 業者特定は招待トークン。email/rc は URL に載せず prefill から取得。
  const token = searchParams.get('token') ?? '';
  const otpCode = searchParams.get('otp') ?? '';

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');

  const { register, handleSubmit, watch, reset, formState: { errors } } = useForm<FormData>({
    defaultValues: { noInvoiceFlag: false, accountType: '0' }
  });
  const noInvoice = watch('noInvoiceFlag');

  // v0.13 要件4: 招待時に既知の会社名/インボイス番号を事前充填（OTPは消費しない）。
  useEffect(() => {
    if (!token) { setError('招待リンクが無効です（トークンがありません）'); return; }
    getVendorPrefill(token)
      .then(r => {
        const d = r.data;
        setEmail(d.email ?? '');
        reset({
          noInvoiceFlag: false,
          accountType: '0',
          companyName: d.companyName ?? '',
          invoiceNumber: d.invoiceNumber ?? undefined,
        } as any);
      })
      .catch((e: any) => setError(e.response?.data?.message ?? '招待情報の取得に失敗しました'));
  }, [token, reset]);

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    setError('');
    try {
      await registerVendor({
        token, otpCode,
        companyName: data.companyName,
        contactName: data.contactName,
        title: data.title,
        invoiceNumber: data.noInvoiceFlag ? undefined : data.invoiceNumber,
        noInvoiceFlag: data.noInvoiceFlag,
        websiteUrl: data.websiteUrl,
        bankName: data.bankName,
        branchName: data.branchName,
        accountType: Number(data.accountType),
        accountNumber: data.accountNumber,
        accountHolder: data.accountHolder,
      });
      navigate('/register/complete');
    } catch (e: any) {
      setError(e.response?.data?.message ?? '登録に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>業者登録</h1>
        <p style={styles.notice}>
          本システムは1社1名のご登録となります。<br />
          複数名への送付を希望される場合は、メーリングリスト等のご活用をお願いします。
        </p>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Section title="会社情報">
            <Field label="会社名 *" error={errors.companyName?.message}>
              <input style={styles.input} {...register('companyName', { required: '必須です' })} />
            </Field>
            <Field label="担当者名 *" error={errors.contactName?.message}>
              <input style={styles.input} {...register('contactName', { required: '必須です' })} />
            </Field>
            <Field label="役職">
              <input style={styles.input} {...register('title')} />
            </Field>
            <Field label="メールアドレス">
              <input style={{ ...styles.input, background: '#f7f7f7' }} value={email} readOnly />
            </Field>
          </Section>

          <Section title="インボイス情報">
            <label style={styles.checkboxLabel}>
              <input type="checkbox" {...register('noInvoiceFlag')} />
              　インボイス番号なし
            </label>
            {!noInvoice && (
              <Field label="インボイス番号（T＋13桁）" error={errors.invoiceNumber?.message}>
                <input
                  style={styles.input}
                  placeholder="T1234567890123"
                  {...register('invoiceNumber', {
                    pattern: { value: /^T\d{13}$/, message: 'T+13桁の数字で入力してください' }
                  })}
                />
              </Field>
            )}
          </Section>

          <Section title="Webサイト・会社案内">
            <Field label="WebサイトURL（任意）">
              <input style={styles.input} type="url" placeholder="https://" {...register('websiteUrl')} />
            </Field>
          </Section>

          <Section title="振込先口座">
            <Field label="銀行名 *" error={errors.bankName?.message}>
              <input style={styles.input} {...register('bankName', { required: '必須です' })} />
            </Field>
            <Field label="支店名 *" error={errors.branchName?.message}>
              <input style={styles.input} {...register('branchName', { required: '必須です' })} />
            </Field>
            <Field label="口座種別 *">
              <select style={styles.input} {...register('accountType')}>
                <option value="0">普通</option>
                <option value="1">当座</option>
              </select>
            </Field>
            <Field label="口座番号（7桁） *" error={errors.accountNumber?.message}>
              <input
                style={styles.input}
                inputMode="numeric"
                maxLength={7}
                {...register('accountNumber', {
                  required: '必須です',
                  pattern: { value: /^\d{7}$/, message: '7桁の数字で入力してください' }
                })}
              />
            </Field>
            <Field label="口座名義（カタカナ） *" error={errors.accountHolder?.message}>
              <input
                style={styles.input}
                placeholder="カブシキガイシャ〇〇"
                {...register('accountHolder', {
                  required: '必須です',
                  pattern: { value: /^[ァ-ヶーヴ・（）()－\-．.／/0-9０-９　\s]+$/, message: 'カタカナで入力してください' }
                })}
              />
            </Field>
          </Section>

          {error && <p style={styles.error}>{error}</p>}
          <button style={styles.button} type="submit" disabled={loading}>
            {loading ? '登録中...' : '登録する'}
          </button>
        </form>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <h3 style={{ borderBottom: '2px solid #2b6cb0', paddingBottom: 6, color: '#2b6cb0', marginBottom: 12 }}>{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, color: '#2d3748', fontSize: 14 }}>{label}</label>
      {children}
      {error && <p style={{ color: '#e53e3e', fontSize: 12, marginTop: 4 }}>{error}</p>}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { minHeight: '100vh', background: '#f0f4f8', padding: '32px 16px' },
  card: { background: '#fff', borderRadius: 12, padding: 32, maxWidth: 600, margin: '0 auto', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' },
  title: { margin: '0 0 8px', fontSize: 22, color: '#1a365d' },
  notice: { background: '#fffbeb', border: '1px solid #f6e05e', borderRadius: 6, padding: 12, fontSize: 13, color: '#744210', marginBottom: 24, lineHeight: 1.6 },
  input: { width: '100%', padding: '9px 12px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: 15, boxSizing: 'border-box' },
  button: { width: '100%', padding: 14, background: '#2b6cb0', color: '#fff', border: 'none', borderRadius: 6, fontSize: 16, cursor: 'pointer', fontWeight: 600, marginTop: 8 },
  error: { color: '#e53e3e', fontSize: 14 },
  checkboxLabel: { display: 'flex', alignItems: 'center', marginBottom: 12, cursor: 'pointer', fontWeight: 600, color: '#2d3748' },
};
